import { z } from 'zod'
import type { StatusTone } from '@/design-system'
import { journalStates, type JournalState } from './smsJournal'

/**
 * Envois groupés par routes (`sms_campaign_*_v1`). Le serveur calcule tout : destinataires, exclusions, texte rendu,
 * segments, coût et obstacles. L'écran affiche, puis renvoie l'aperçu vu (version du modèle, nombre de numéros) :
 * la confirmation est refusée si l'un ou l'autre a changé entre-temps.
 */

export const exclusionCodes = [
  'NO_CONSENT',
  'OPTED_OUT',
  'NO_NUMBER',
  'INVALID_NUMBER',
  'AMBIGUOUS',
  'NOT_IN_PILOT',
  'DUPLICATE',
] as const
export type ExclusionCode = (typeof exclusionCodes)[number]

export const campaignBlockers = [
  'MODULE_DISABLED',
  'SENDING_PAUSED',
  'NO_ADMISSIBLE',
  'OVER_DAILY_CAP',
] as const
export type CampaignBlocker = (typeof campaignBlockers)[number]

const countsSchema = z.record(z.string(), z.number().int())

export const campaignOptionsSchema = z.object({
  seasons: z.array(z.string()),
  season: z.string().nullable(),
  routes: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      color: z.string().nullable(),
      addressCount: z.number().int(),
    }),
  ),
  templates: z.array(
    z.object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
      version: z.number().int(),
      body: z.string(),
      allowedVariables: z.array(z.string()),
    }),
  ),
  inactiveTemplates: z.array(z.string()),
})
export type CampaignOptions = z.infer<typeof campaignOptionsSchema>
export type CampaignTemplate = CampaignOptions['templates'][number]

export const campaignPreviewSchema = z.object({
  season: z.string(),
  routeIds: z.array(z.string()),
  routeNames: z.array(z.string()),
  templateId: z.string(),
  templateKey: z.string(),
  templateName: z.string(),
  templateVersion: z.number().int(),
  announcedDate: z.string(),
  announcedTime: z.string().nullable(),
  body: z.string(),
  segments: z.number().int(),
  costMicros: z.number().nullable(),
  sendNow: z.boolean(),
  notBefore: z.string(),
  expiresAt: z.string(),
  addressCount: z.number().int(),
  admissible: z.number().int(),
  exclusions: countsSchema,
  mode: z.string(),
  senderE164: z.string().nullable(),
  dailyCap: z.number().int().nullable(),
  sentToday: z.number().int(),
  blockers: z.array(z.string()),
  confirmationText: z.string(),
})
export type CampaignPreview = z.infer<typeof campaignPreviewSchema>

export const campaignRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  season: z.string(),
  routeNames: z.array(z.string()),
  templateVersion: z.number().int(),
  announcedDate: z.string(),
  announcedTime: z.string().nullable(),
  body: z.string(),
  notBefore: z.string(),
  expiresAt: z.string(),
  addressCount: z.number().int(),
  admissible: z.number().int(),
  createdAt: z.string(),
  authorName: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  counts: countsSchema,
})
export type CampaignRow = z.infer<typeof campaignRowSchema>
export const campaignListSchema = z.array(campaignRowSchema)

export const campaignDetailSchema = campaignRowSchema.extend({
  confirmationText: z.string(),
  exclusions: countsSchema,
  recipients: z.array(
    z.object({
      id: z.string(),
      routeName: z.string().nullable(),
      address: z.string().nullable(),
      clientName: z.string().nullable(),
      e164: z.string().nullable(),
      exclusion: z.string().nullable(),
      outboxId: z.string().nullable(),
      state: z.enum(journalStates).nullable(),
      reason: z.string().nullable(),
      updatedAt: z.string().nullable(),
    }),
  ),
})
export type CampaignDetail = z.infer<typeof campaignDetailSchema>
export type CampaignRecipient = CampaignDetail['recipients'][number]

/** Saisie de l'assistant. Heures locales « AAAA-MM-JJTHH:MI », lues par le serveur dans le fuseau de RECA. */
export interface CampaignDraft {
  season: string
  routeIds: string[]
  templateId: string
  announcedDate: string
  announcedTime: string
  sendAt: string | null
  expiresAt: string
}

export interface CampaignCommand {
  season: string
  routeIds: string[]
  templateId: string
  announcedDate: string
  announcedTime: string | null
  sendAt: string | null
  expiresAt: string
}

export function campaignCommand(draft: CampaignDraft): CampaignCommand {
  return {
    season: draft.season,
    routeIds: draft.routeIds,
    templateId: draft.templateId,
    announcedDate: draft.announcedDate,
    announcedTime: draft.announcedTime.trim() === '' ? null : draft.announcedTime,
    sendAt: draft.sendAt,
    expiresAt: draft.expiresAt,
  }
}

const exclusionDisplay: Record<ExclusionCode, { label: string; why: string }> = {
  NO_CONSENT: {
    label: 'Sans consentement',
    why: 'Aucune preuve d’inscription aux alertes opérationnelles pour ce numéro.',
  },
  OPTED_OUT: { label: 'Désinscrits', why: 'STOP reçu, retrait traité par un agent ou blocage chez Telnyx.' },
  NO_NUMBER: { label: 'Numéro absent', why: 'Aucun téléphone au dossier du client.' },
  INVALID_NUMBER: {
    label: 'Numéro invalide',
    why: 'Téléphone au dossier qui n’est pas un numéro canadien ou américain à 10 chiffres.',
  },
  AMBIGUOUS: {
    label: 'Association ambiguë',
    why: 'Numéro présent dans plusieurs dossiers, sans association confirmée pour ce client.',
  },
  NOT_IN_PILOT: {
    label: 'Hors liste du pilote',
    why: 'Mode pilote : seuls les numéros d’équipe autorisés reçoivent un SMS.',
  },
  DUPLICATE: {
    label: 'Doublons fusionnés',
    why: 'Même numéro pour plusieurs adresses : un seul SMS, le texte étant identique.',
  },
}

export function exclusionLabel(code: string): string {
  return (exclusionDisplay as Record<string, { label: string } | undefined>)[code]?.label ?? code
}

/** Exclusions dans l'ordre de la maquette, sans les catégories vides. */
export function exclusionRows(
  counts: Record<string, number>,
): { code: string; n: number; label: string; why: string }[] {
  return exclusionCodes
    .filter((code) => (counts[code] ?? 0) > 0)
    .map((code) => ({ code, n: counts[code] ?? 0, ...exclusionDisplay[code] }))
}

export function exclusionTotal(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0)
}

const blockerMessages: Record<CampaignBlocker, string> = {
  MODULE_DISABLED: 'Envoi impossible : le module SMS est désactivé.',
  SENDING_PAUSED: 'Envoi impossible : les nouveaux envois sont suspendus (Paramètres SMS).',
  NO_ADMISSIBLE: 'Envoi impossible : aucun numéro admissible dans les routes choisies.',
  OVER_DAILY_CAP: 'Envoi impossible : le plafond quotidien serait dépassé.',
}

export function blockerMessage(
  blocker: string,
  preview?: Pick<CampaignPreview, 'dailyCap' | 'sentToday'>,
): string {
  if (blocker === 'OVER_DAILY_CAP' && preview?.dailyCap != null) {
    return `Envoi impossible : plafond de ${String(preview.dailyCap)} SMS par jour (${String(preview.sentToday)} déjà envoyés aujourd’hui).`
  }
  return (blockerMessages as Record<string, string | undefined>)[blocker] ?? `Envoi impossible (${blocker}).`
}

/** Avertissement du mode, distinct d'un obstacle : en pilote, l'envoi part mais seulement vers l'équipe. */
export function modeNotice(mode: string): string | null {
  if (mode === 'TEST') return 'Mode pilote : seuls les numéros d’équipe autorisés reçoivent un SMS réel.'
  if (mode === 'SIMULATION') return 'Mode simulation : aucun SMS réel ne part, le parcours est simulé.'
  return null
}

export function costLabel(costMicros: number | null): string {
  if (costMicros === null) return 'Non disponible'
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'USD' }).format(costMicros / 1_000_000)
}

const settled: JournalState[] = [
  'DELIVERED',
  'UNCONFIRMED',
  'DELIVERY_FAILED',
  'SEND_FAILED',
  'BLOCKED',
  'CANCELLED',
  'EXPIRED',
]

/** Destinataires qui ont un résultat final (livré, échec, bloqué, annulé, expiré, non confirmé). */
export function settledCount(counts: Record<string, number>): number {
  return settled.reduce((sum, state) => sum + (counts[state] ?? 0), 0)
}

/** Intentions encore annulables : en file locale (programmées ou prêtes), jamais une soumise. */
export function cancellableCount(counts: Record<string, number>): number {
  return (counts.SCHEDULED ?? 0) + (counts.PREPARED ?? 0)
}

/** État d'ensemble : un envoi partiel ne s'affiche jamais « Succès ». */
export function campaignState(row: Pick<CampaignRow, 'counts' | 'cancelledAt' | 'admissible'>): {
  label: string
  tone: StatusTone
} {
  const { counts } = row
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0)
  const delivered = counts.DELIVERED ?? 0
  const pending = cancellableCount(counts) + (counts.SUBMITTED ?? 0) + (counts.UNCERTAIN ?? 0)
  const problems = total - delivered - pending - (counts.CANCELLED ?? 0)
  if (row.cancelledAt) {
    return {
      label: `Annulé · ${String(delivered)} livré${delivered > 1 ? 's' : ''}, ${String(counts.CANCELLED ?? 0)} annulé${(counts.CANCELLED ?? 0) > 1 ? 's' : ''}`,
      tone: 'neutral',
    }
  }
  if (total > 0 && (counts.SCHEDULED ?? 0) === total) return { label: 'Programmé', tone: 'info' }
  if (pending > 0) {
    return {
      label: delivered > 0 || problems > 0 ? 'Partiellement envoyé · en cours' : 'En cours',
      tone: 'warning',
    }
  }
  if (total > 0 && delivered === total) return { label: 'Tous livrés', tone: 'success' }
  return {
    label: `Terminé · ${String(delivered)}/${String(total)} livrés`,
    tone: problems > 0 ? 'warning' : 'neutral',
  }
}

/** Résultats par état, ordre de la maquette. */
export const resultStates: JournalState[] = [
  'DELIVERED',
  'SUBMITTED',
  'PREPARED',
  'SCHEDULED',
  'UNCONFIRMED',
  'UNCERTAIN',
  'DELIVERY_FAILED',
  'SEND_FAILED',
  'BLOCKED',
  'CANCELLED',
  'EXPIRED',
]

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** Heure locale de Montréal « AAAA-MM-JJTHH:MI », décalée de `minutes`. */
export function torontoLocal(now: Date = new Date(), minutes = 0): string {
  const shifted = new Date(now.getTime() + minutes * 60_000)
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(shifted)
      .map((part) => [part.type, part.value]),
  ) as Record<string, string>
  return `${parts.year ?? ''}-${parts.month ?? ''}-${parts.day ?? ''}T${parts.hour ?? ''}:${parts.minute ?? ''}`
}

/** Ajoute des minutes à une heure locale « AAAA-MM-JJTHH:MI » (calcul calendaire, sans fuseau). */
export function addLocalMinutes(local: string, minutes: number): string {
  const [date = '', time = '00:00'] = local.split('T')
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number)
  const [hh = 0, mm = 0] = time.split(':').map(Number)
  const value = new Date(Date.UTC(y, m - 1, d, hh, mm) + minutes * 60_000)
  return `${String(value.getUTCFullYear())}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}T${pad(value.getUTCHours())}:${pad(value.getUTCMinutes())}`
}

/**
 * Expiration proposée : l'heure annoncée de l'opération (une annonce lue après le début n'a plus de sens), sinon la
 * fin du jour annoncé ; jamais moins d'une heure après l'envoi. L'utilisateur peut la changer, le serveur la borne.
 */
export function suggestedExpiry(
  draft: Pick<CampaignDraft, 'announcedDate' | 'announcedTime' | 'sendAt'>,
  now: string,
): string {
  const announced = draft.announcedDate
    ? `${draft.announcedDate}T${draft.announcedTime.trim() === '' ? '23:59' : draft.announcedTime}`
    : ''
  const floor = addLocalMinutes(draft.sendAt ?? now, 60)
  return announced > floor ? announced : floor
}

/** Erreurs de saisie avant l'aperçu serveur (le serveur refait chaque contrôle). */
export function draftErrors(
  draft: CampaignDraft,
  template: CampaignTemplate | undefined,
  now: string,
): Partial<
  Record<'routes' | 'template' | 'announcedDate' | 'announcedTime' | 'sendAt' | 'expiresAt', string>
> {
  const errors: Partial<
    Record<'routes' | 'template' | 'announcedDate' | 'announcedTime' | 'sendAt' | 'expiresAt', string>
  > = {}
  if (draft.routeIds.length === 0) errors.routes = 'Choisissez au moins une route.'
  if (!template) errors.template = 'Choisissez un modèle actif.'
  const today = now.slice(0, 10)
  if (!draft.announcedDate) errors.announcedDate = 'Date de l’opération obligatoire.'
  else if (draft.announcedDate < today) errors.announcedDate = 'Date passée.'
  else if (draft.announcedDate > addLocalMinutes(`${today}T00:00`, 14 * 24 * 60).slice(0, 10)) {
    errors.announcedDate = 'Au plus 14 jours à l’avance.'
  }
  if (template && /\{\{\s*heure\s*\}\}/.test(template.body) && draft.announcedTime.trim() === '') {
    errors.announcedTime = 'Ce modèle annonce une heure : indiquez l’heure estimée.'
  }
  if (draft.sendAt !== null && draft.sendAt < now) errors.sendAt = 'Heure d’envoi passée.'
  const start = draft.sendAt ?? now
  if (!draft.expiresAt) errors.expiresAt = 'Expiration obligatoire.'
  else if (draft.expiresAt < addLocalMinutes(start, 15))
    errors.expiresAt = 'Au moins 15 minutes après l’envoi.'
  else if (draft.expiresAt > addLocalMinutes(start, 3 * 24 * 60))
    errors.expiresAt = 'Au plus 3 jours après l’envoi.'
  return errors
}

const monthNames = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

/** Même écriture que le serveur (`sms_fr_date_v1`, `sms_fr_time_v1`) pour l'aperçu avant vérification. */
export function frenchDate(isoDate: string): string {
  const [, month = 1, day = 1] = isoDate.split('-').map(Number)
  return `${day === 1 ? '1er' : String(day)} ${monthNames[month - 1] ?? ''}`
}

export function frenchTime(time: string): string {
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  return `${String(hours)} h${minutes === 0 ? '' : ` ${pad(minutes)}`}`
}

/** « 18 novembre 2026, 06:00 » pour une heure locale « AAAA-MM-JJTHH:MI ». */
export function formatLocal(local: string): string {
  const [date = '', time = ''] = local.split('T')
  return `${frenchDate(date)} ${date.slice(0, 4)}, ${time}`
}
